import { Users, Handshake, LineChart, DollarSign, Target, Shield, Link as LinkIcon, BarChart3, Database } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "Affiliate Marketing Systems Built for Predictable Growth",
  seoDescription: "Build and scale an affiliate program that drives predictable, profitable growth through quality partnerships and proper tracking.",
  canonicalUrl: "https://xperfomax.com/affiliate-marketing",
  category: "services",
  categoryLabel: "Services / Affiliate Marketing",
  headline: "Affiliate Marketing Systems Built for Predictable Growth",
  subheadline: "We build and optimize affiliate programs that drive predictable, profitable growth through quality partnerships and proper attribution.",
  ctaText: "Start a serious conversation",
  problemTitle: "Why Affiliate Programs Fail",
  problems: [
    "Partner quantity is prioritized over quality, inviting fraud.",
    "Attribution gaps make it impossible to optimize payouts.",
    "Commission structures incentivize volume, not profitability.",
    "Programs launch but partners receive no ongoing support.",
  ],
  systemTitle: "Our Affiliate System",
  systemDescription: "Partner quality, accurate tracking, aligned incentives, and ongoing optimization.",
  services: [
    { name: "Program Setup & Strategy", icon: "Target", components: ["Program structure design", "Commission model development", "Platform selection & setup"], outcome: "A program built for scalable partner acquisition." },
    { name: "Affiliate Onboarding", icon: "Users", components: ["Partner vetting & approval", "Onboarding automation", "Resource & creative provision"], outcome: "Quality partners ready to promote effectively." },
    { name: "Tracking & Attribution", icon: "LineChart", components: ["Multi-touch attribution setup", "Cross-device tracking", "Fraud detection & prevention"], outcome: "Accurate tracking you can trust for payouts." },
    { name: "Payout Optimization", icon: "DollarSign", components: ["Commission structure optimization", "Tiered incentive programs", "Performance-based bonuses"], outcome: "Incentives that align partner and business goals." },
    { name: "Partner Relationship Management", icon: "Handshake", components: ["Ongoing partner communication", "Performance coaching", "Exclusive partner opportunities"], outcome: "Long-term partnerships, not transactional relationships." },
    { name: "Performance Monitoring", icon: "Shield", components: ["Real-time performance dashboards", "Fraud monitoring & prevention", "ROI & contribution analysis"], outcome: "Full visibility into program health and profitability." },
  ],
  processTitle: "From setup to scale.",
  processSteps: [
    { number: "01", title: "Program Audit or Design", description: "Evaluate existing program or design new program structure, commissions, and partner criteria." },
    { number: "02", title: "Platform & Tracking Setup", description: "Configure affiliate platform, tracking, attribution, and fraud prevention systems." },
    { number: "03", title: "Partner Recruitment", description: "Identify, vet, and onboard high-quality affiliates aligned with your brand and audience." },
    { number: "04", title: "Activation & Support", description: "Provide partners with resources, training, and ongoing support to drive performance." },
    { number: "05", title: "Optimization & Scale", description: "Analyze performance, optimize commission structures, and scale with top performers." },
  ],
  technologyCategories: [
    { title: "Affiliate Platforms", icon: "Link", items: ["Impact", "PartnerStack", "ShareASale", "CJ Affiliate"] },
    { title: "Tracking", icon: "Target", items: ["HasOffers", "Everflow", "Post Affiliate Pro"] },
    { title: "Analytics", icon: "BarChart3", items: ["GA4", "Looker Studio", "Custom Dashboards"] },
  ],
  techTitle: "Affiliate Platforms",
  techDescription: "Enterprise-grade affiliate platforms and tracking solutions.",
  qualification: {
    forYou: [
      "You have a proven product with healthy margins",
      "You're ready to invest in building partner relationships",
      "You understand that affiliate marketing is a long-term channel",
      "You have resources to support partner success",
    ],
    notForYou: [
      "You expect affiliates to drive sales with no support",
      "You're not willing to pay fair commissions",
      "You want overnight results from affiliate marketing",
      "You're not prepared to vet and manage partner quality",
    ],
  },
  faqs: [
    { question: "How long does it take to build a successful affiliate program?", answer: "Program setup takes 4-8 weeks. Building a productive partner base typically takes 3-6 months of consistent recruitment and optimization." },
    { question: "How do you prevent affiliate fraud?", answer: "We implement multi-layer fraud detection including traffic analysis, conversion patterns, and manual review. We also structure programs to discourage fraudulent behavior." },
    { question: "What commission rates should we offer?", answer: "Commission rates depend on your margins, customer LTV, and competitive landscape. We model different scenarios to find the optimal structure for your business." },
    { question: "Can you manage our existing affiliate program?", answer: "Yes. We can audit and optimize existing programs or build from scratch. We often find significant improvement opportunities in established programs." },
  ],
  finalCtaTitle: "Build a scalable partner ecosystem.",
  finalCtaDescription: "Affiliate marketing done right is a predictable revenue channel. Let's discuss your program.",
  finalCtaButtonText: "Start a serious conversation",
};

const AffiliateMarketing = () => {
  const { content } = usePageContent("affiliate-marketing", fallback);

  const services = content.services.map((s: any) => ({
    ...s,
    icon: resolveIcon(s.icon),
  }));

  const technologyCategories = content.technologyCategories.map((t: any) => ({
    ...t,
    icon: resolveIcon(t.icon),
  }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default AffiliateMarketing;
